import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Mic, FileText, ListChecks, PenLine, Youtube, Film, MessageSquare,
  Library, LineChart, Image as ImageIcon, AudioLines, Gauge,
  ArrowRight, ChevronDown, Check, Quote, Sparkles,
  Zap, ShieldCheck, UserCheck,
} from 'lucide-react';
import SEO from '../../components/SEO';
import Section from '../../components/v2/Section';
import V2Hero from '../../components/v2/V2Hero';
import Reveal from '../../components/v2/Reveal';
import ImageMeta from '../../components/v2/ImageMeta';
import { HERO } from '../../data/heroImages';
import HeroCollage from '../../components/v2/HeroCollage';
import WeeklyShippingWall from '../../components/v2/WeeklyShippingWall';
import LatestFromBlog from '../../components/v2/LatestFromBlog';
import { NewsletterModal } from '../../components/v2/NewsletterSignup';
import { PrimaryCTA, CTABlock } from '../../components/v2/CTAButton';
import { ServicesGrid, WhoWeWorkWith, HeroServiceLine, CompoundSection, FounderStrip } from '../../components/v2/HomeSections';
import FAQSchema from '../../components/FAQSchema';

// Hero image is sourced from /data/heroImages.js so alt/credit/dimensions
// stay consistent sitewide. Was a generic Macbook + coffee shot — now a warm
// home podcast desk.

const DELIVERABLES = [
  { icon: Mic,        label: 'Podcast' },
  { icon: FileText,   label: 'Transcript' },
  { icon: ListChecks, label: 'Show notes' },
  { icon: PenLine,    label: 'Blog' },
  { icon: Youtube,    label: 'YouTube' },
  { icon: ImageIcon,  label: 'Thumbnails' },
  { icon: Film,       label: 'Daily Shorts' },
  { icon: MessageSquare, label: 'Community posts' },
  { icon: Library,    label: 'Business Website' },
  { icon: LineChart,  label: 'GSC monitoring' },
  { icon: AudioLines, label: 'Voice & AI search' },
  { icon: Gauge,      label: 'Performance' },
];

const PAINS = [
  'You record things, then they sit in drafts.',
  'You post in bursts, then disappear for weeks.',
  'You know you should be on YouTube and podcasts, but it’s too time-consuming.',
  'Your content is scattered across platforms with no home base.',
  'You don’t want to learn editing tools or chase trends.',
  'You want visibility that compounds, not random posts.',
];

const WEEKLY = [
  { icon: Mic,           title: '1 podcast episode',              desc: 'Produced and published, every week.' },
  { icon: FileText,      title: '1 edited transcript',            desc: 'Cleaned up and ready to repurpose.' },
  { icon: ListChecks,    title: '1 tailored show notes page',     desc: 'Optimised for search, easy to scan.' },
  { icon: PenLine,       title: '1 blog post',                    desc: 'On-brand, SEO-aware, on your site.' },
  { icon: Youtube,       title: '1 YouTube video + description',  desc: 'Optimised title, description, tags.' },
  { icon: ImageIcon,     title: 'Eye-catching thumbnails',        desc: 'Custom podcast art + scroll-stopping YouTube thumbnails.' },
  { icon: Film,          title: '21 Shorts per week',             desc: '3 per day, all on-brand, with descriptions.' },
  { icon: MessageSquare, title: '7 YouTube Community posts',      desc: 'One per day to keep your channel warm.' },
  { icon: Library,       title: 'Business website updates',       desc: 'Your owned content library, kept fresh.' },
  { icon: LineChart,     title: 'Google Search Console',          desc: 'GSC + Bing Webmaster reviewed weekly.' },
  { icon: AudioLines,    title: 'Voice & AI search',              desc: 'Found on Siri, Hey Google, ChatGPT, Perplexity.' },
  { icon: Gauge,         title: 'Site performance',               desc: 'Core Web Vitals + uptime watched weekly.' },
];

const STEPS = [
  { n: 1, title: 'Record',  desc: 'We guide the structure each week. You just press record.' },
  { n: 2, title: 'We produce everything', desc: 'Editing, transcripts, show notes, blog, Shorts, posts, Hub updates, all of it, done for you.' },
  { n: 3, title: 'We publish, every week', desc: 'Consistent, compounding presence. Cancel any time after the 4-week minimum — most clients stay six months because that\u2019s when the compounding really shows up.' },
];

const TESTIMONIALS = [
  {
    quote: 'Absolutely the best!!! If you are looking for a podcast producer, look no further!!! Jo has been instrumental in getting The Scrumptious Woman off the ground in no time. Less than 1 month from first speaking it was launched. Within 2 months we are ranking in the top 10% worldwide.',
    name: 'Juliette Karaman',
    role: 'The Scrumptious Woman',
  },
  {
    quote: 'Jo, you have been such a fantastic part of my publishing journey. Thanks for your amazing formatting skills, guidance and reassurance with my book. Thanks for your time and enthusiasm with my audiobook preparations too. You’re so professional, thoughtful and inspiring.',
    name: 'Jennifer Jones',
    role: 'Poet & Author',
  },
  {
    quote: 'I’m so grateful to have entered Jo’s world. Being introduced to Jo was one of those life-changing moments. I was instantly put at ease by her professional approach and able to complete my project quickly and effortlessly thanks to her skills.',
    name: 'Vivienne Rawnsley',
    role: 'Hypnotherapist',
  },
];

// Hero testimonial for the homepage. VERBATIM opening of Lucy Rennie's
// real testimonial on /results (Results.js line 40). Jo-approved wording.
const HERO_TESTIMONIAL = {
  quote: "I couldn\u2019t have done it without them. Superb! I highly recommend Jo and the team who have been absolutely amazing from the start.",
  author: 'Lucy Rennie',
  role: 'Host, Future Proof your Business Podcast',
};

const FAQS = [
  { q: 'Who is this for?', a: 'Small business owners who already know they need to be visible online but don’t have the time, team, or appetite to do the production themselves. Coaches, consultants, studio owners, founders, anyone with expertise worth amplifying.' },
  { q: 'Do you work remotely?', a: 'Yes. The whole engine runs remotely. You record from wherever you are, we produce and publish from wherever we are. We schedule a weekly check-in to keep things smooth.' },
  { q: 'Do you use AI?', a: 'We use AI where it speeds up production without compromising your voice, transcript cleanup, draft show notes, image generation for thumbnails. A human is in the loop on everything that goes out under your name.' },
  { q: 'What do I need to do weekly?', a: 'One recording session, roughly 30–60 minutes, plus a 15-minute check-in with us. That’s your whole content week.' },
  { q: 'What is an Authority Hub?', a: 'It\u2019s our name for a proper business website that organises your podcast, books, YouTube, blog and services into one searchable "listen, watch, learn" home you own. Social posts disappear \u2014 a real business website compounds. Included in the Pro Plan, or built as a standalone project from \u00a3495 one-off.' },
  { q: 'What does SEO monitoring include?', a: 'Full Google Search Console + Bing Webmaster monitoring every week: rankings on your target keywords, indexing/coverage issues, click-through rate, backlinks, and crawl errors. Anything that drops, we spot fast and flag inside your weekly check-in.' },
  { q: 'Will I be found on Siri, Hey Google and ChatGPT?', a: 'Yes, that is a specific focus of the Pro Plan. We optimise your transcripts, show notes, business website schema and structured data so voice assistants (Siri, Hey Google, Alexa) and AI search engines (ChatGPT, Perplexity, Claude, Gemini) can cite you as a source. Voice and AI search now account for a rapidly growing slice of how customers actually find businesses, we build for both, not just classic Google.' },
  { q: 'What is "site performance monitoring"?', a: 'We track your site’s Core Web Vitals (loading speed, interactivity, layout stability), uptime, and mobile experience every week. If anything degrades, a slow-loading image, an outage, a broken link, you’ll hear from us with a quick-fix plan. Performance is table-stakes for both classic Google ranking AND AI-search citation.' },
  { q: 'Can you work with Riverside?', a: 'Yes, Riverside, Zoom, SquadCast, in-person recordings. If it produces a clean audio file, we can work with it.' },
  { q: 'What happens on the call?', a: 'A 30-minute fit call. We confirm your goals, walk through what the Pro Plan would actually look like for your business, and answer anything. No pressure, no pitch.' },
  { q: 'Do you still offer book publishing or audiobook recording?', a: 'Yes. Our public offer is the Pro Plan, but Jo still personally takes on a small number of bespoke book publishing and audiobook projects each year. That includes formatting, cover, Amazon KDP launch, narration coaching, studio-quality recording and Audible/ACX distribution. Book a 30-minute call and Jo will send you a bespoke quote.' },
  { q: 'Can you manage my existing podcast (not the full Pro Plan)?', a: 'Yes. If you already have a podcast and just need editing, publishing, show notes and growth support without the wider Pro Plan engine, Jo will build a custom podcast management quote for you. Book a call and we will tailor the scope to your show.' },
  { q: 'Do you still build websites or apps?', a: 'Yes. We take on a small number of website and PWA/app builds each year alongside the Pro Plan, marketing sites, membership portals, custom PWAs. There is a quick overview at /websites-apps, and the best next step is a 30-minute call so Jo can scope the project and send a bespoke quote.' },
];

const faqsForSchema = FAQS.map(f => ({ question: f.q, answer: f.a }));

export default function ProHome() {
  return (
    <main className="font-body text-[#475569] bg-[#F6F8FB]">
      <SEO
        title="Audio & Co: UK Podcast Production & Management"
        description="Audio & Co: podcast management, book publishing, audiobooks, YouTube and business websites built for SEO. Expert support for UK coaches and authors."
        keywords={['done for you podcast production', 'podcast production service uk', 'content repurposing service', 'content repurposing agency', 'youtube management for coaches', 'podcast to youtube shorts service', 'content marketing for coaches uk', 'podcast production agency', 'business website for coaches', 'done for you content marketing']}
        url="https://audioandco.com/"
      />
      <FAQSchema faqs={faqsForSchema} pageUrl="https://audioandco.com/" />

      {/* HERO — home podcast desk + floating collage of the 12 outputs */}
      <V2Hero
        image={HERO.homePodcastDesk}
        testId="home-hero"
        overlayLeft={0.94}
        overlayMid={0.55}
        overlayRight={0.15}
        rightVisual={<HeroCollage podcastCover="https://is1-ssl.mzstatic.com/image/thumb/Podcasts211/v4/94/15/87/941587b0-5574-1a38-c078-1378079809fe/mza_11066063311020046754.jpg/300x300bb.jpg" />}
      >
        <div className="max-w-2xl">
          <Reveal className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#E2E8F0] text-xs font-medium text-[#475569] mb-6 shadow-[0_4px_16px_rgba(30,42,68,0.06)]">
            <Sparkles className="w-3.5 h-3.5 text-[#BC516F]" />
            Done-for-you podcast &amp; content service · 4-week minimum, cancel any time
          </Reveal>
          {/* HERO — rebuilt Sep 2026 per Jo's spec:
              - Exactly ONE H1 on the page
              - NO £280/week, £7,280 or lock-in language above fold
              - Deliverables in plain English, not counts of Shorts
              - "Check if we're a fit" is the sitewide-locked primary CTA */}
          <Reveal delay={1} as="h1" className="font-display font-bold text-[#1E2A44] text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
            Done-for-you podcast production that turns into a{' '}
            <span className="v2-shimmer">full visibility engine</span>.
          </Reveal>
          <Reveal delay={2} as="p" className="text-lg sm:text-xl text-[#475569] mt-6 leading-relaxed">
            One weekly recording becomes your podcast episode, YouTube video, Shorts and clips, show notes and an SEO blog post. Published for you, in your voice.
          </Reveal>
          <Reveal delay={3} as="p" className="text-[#1E2A44] font-display font-semibold mt-5 text-lg">
            Record once. We handle the rest. You get your time back.
          </Reveal>
          <Reveal delay={3} className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-8">
            <CTABlock size="lg" testIdPrefix="hero-cta" />
            <a
              href="#how-it-works"
              className="text-[#1E2A44] font-semibold underline decoration-2 underline-offset-4 hover:text-[#BC516F] transition"
              data-testid="hero-how-it-works"
            >
              See how it works
            </a>
          </Reveal>
          {/* Trust strip — mirrors the /podcast-visibility-package hero.
              Keeps the "cancel after week 4" reassurance visible before
              the visitor even navigates. Sep 2026. */}
          <div className="mt-6 border-l-4 border-[#BC516F] bg-white/70 backdrop-blur-sm rounded-r-[10px] px-5 py-4 max-w-2xl" data-testid="home-hero-trust-strip">
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#475569]">
              <span className="inline-flex items-center gap-1.5 text-[#1E2A44] font-semibold"><Zap className="w-4 h-4 text-[#BC516F]" fill="currentColor" />Weekly turnaround, guaranteed</span>
              <span className="inline-flex items-center gap-1.5 text-[#1E2A44] font-semibold"><ShieldCheck className="w-4 h-4 text-[#A7468A]" />Zero editing, zero tech</span>
              <span className="inline-flex items-center gap-1.5 text-[#1E2A44] font-semibold"><UserCheck className="w-4 h-4 text-[#81162A]" />Human editors, one point of contact</span>
            </div>
          </div>
          <HeroServiceLine />
          {/* Hero client testimonial — brand-search visitors land here
              first, so social proof needs to be above the fold. */}
          {HERO_TESTIMONIAL.quote && (
            <figure className="mt-5 bg-white/90 backdrop-blur-sm border border-[#E2E8F0] rounded-[14px] p-5 max-w-2xl shadow-[0_10px_30px_rgba(30,42,68,0.06)]" data-testid="home-hero-testimonial">
              <Quote className="w-6 h-6 text-[#BC516F] mb-2" />
              <blockquote className="text-[#1E2A44] font-display font-semibold text-[17px] leading-snug">
                &ldquo;{HERO_TESTIMONIAL.quote}&rdquo;
              </blockquote>
              {(HERO_TESTIMONIAL.author || HERO_TESTIMONIAL.role) && (
                <figcaption className="mt-3 text-sm text-[#475569]">
                  {HERO_TESTIMONIAL.author && <span className="font-semibold text-[#1E2A44]">{HERO_TESTIMONIAL.author}</span>}
                  {HERO_TESTIMONIAL.author && HERO_TESTIMONIAL.role && <span> · </span>}
                  {HERO_TESTIMONIAL.role && <span>{HERO_TESTIMONIAL.role}</span>}
                </figcaption>
              )}
            </figure>
          )}
        </div>
      </V2Hero>

      <ServicesGrid />

      {/* Anchor for the hero "See how it works" jump link */}
      <div id="how-it-works" style={{ scrollMarginTop: 96 }} aria-hidden="true"></div>

      {/* DELIVERABLES PREVIEW ROW (was inside hero, now its own tight strip) */}
      <Section bg="white" innerClassName="!py-10 sm:!py-12" testId="home-deliverables-row">
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
          {DELIVERABLES.map((d, i) => {
            const Icon = d.icon;
            return (
              <div key={i} className="bg-white rounded-[14px] border border-[#E2E8F0] p-3 sm:p-4 flex flex-col items-center text-center">
                <Icon className="w-5 h-5 text-[#BC516F] mb-2" />
                <span className="text-xs sm:text-[13px] font-medium text-[#1E2A44] leading-tight">{d.label}</span>
              </div>
            );
          })}
        </div>
      </Section>

      {/* DOES THIS SOUND FAMILIAR */}
      <Section bg="white" testId="home-pains">
        <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight max-w-3xl">
          Does this sound familiar?
        </h2>
        <p className="mt-3 text-[#64748B] text-lg max-w-2xl">If you nod at three or more of these, the Pro Plan is built for you.</p>
        <ul className="mt-10 grid sm:grid-cols-2 gap-4">
          {PAINS.map((p, i) => (
            <li key={i} className="flex items-start gap-3 bg-[#F6F8FB] border border-[#E2E8F0] rounded-[14px] p-5">
              <span className="w-6 h-6 shrink-0 rounded-full bg-white border border-[#E2E8F0] flex items-center justify-center text-[#BC516F] text-sm">·</span>
              <span className="text-[#475569] leading-relaxed">{p}</span>
            </li>
          ))}
        </ul>
      </Section>

      {/* Weekly shipping wall — real Katie + Sarah episodes, live proof */}
      <WeeklyShippingWall />

      {/* WEEKLY DELIVERABLES — premium dark section, cascading spotlight */}
      <Section bg="dark" className="overflow-hidden" testId="home-weekly" innerClassName="!py-20 sm:!py-24 relative">
        <div className="absolute -top-24 -left-16 w-[520px] h-[520px] rounded-full bg-[#A7468A]/22 blur-3xl pointer-events-none v2-blob" aria-hidden="true" />
        <div className="absolute -bottom-24 -right-16 w-[420px] h-[420px] rounded-full bg-[#BC516F]/18 blur-3xl pointer-events-none v2-blob" style={{ animationDelay: '4s' }} aria-hidden="true" />
        <div className="relative max-w-3xl">
          <p className="text-[#E0A8B8] font-semibold text-sm uppercase tracking-[0.2em] flex items-center gap-3">
            <span className="w-8 h-px bg-[#E0A8B8]/60" />
            Every week, like clockwork
          </p>
          <h2 className="font-display font-bold text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4 leading-[1.05]">
            One recording becomes <span className="v2-shimmer">twelve outputs</span>.
          </h2>
          <p className="mt-5 text-white/70 text-lg leading-relaxed max-w-2xl">
            Every deliverable below ships every single week &mdash; on your podcast feed, YouTube channel, blog, socials, and inside Siri, Google Assistant, ChatGPT and Perplexity.
          </p>
        </div>
        <div className="relative mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {WEEKLY.map((w, i) => {
            const Icon = w.icon;
            const num = String(i + 1).padStart(2, '0');
            return (
              <div
                key={i}
                className="v2-cascade group relative bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-[16px] p-6 sm:p-7 flex flex-col overflow-hidden"
                style={{ '--card-index': i }}
                data-testid={`home-weekly-${i}`}
              >
                <div className="absolute top-4 right-5 v2-serif-accent text-white/25 text-3xl select-none pointer-events-none">
                  {num}
                </div>
                <div
                  className="v2-cascade-orb w-12 h-12 rounded-[12px] flex items-center justify-center shadow-[0_10px_24px_-6px_rgba(188,81,111,0.5)]"
                  style={{ background: 'linear-gradient(135deg, #BC516F 0%, #A7468A 55%, #81162A 100%)' }}
                >
                  <Icon className="w-6 h-6 text-white" strokeWidth={2} />
                </div>
                <h3 className="font-display font-semibold text-white text-lg leading-tight mt-5">{w.title}</h3>
                <p className="text-white/65 mt-2 leading-relaxed text-[15px] flex-1">{w.desc}</p>
              </div>
            );
          })}
        </div>
      </Section>

      {/* TIME BACK, supporting visual — cosy home vibe, big editorial layout */}
      <Section bg="white" innerClassName="!py-0" testId="home-time-back">
        <Reveal className="grid lg:grid-cols-2 gap-0 rounded-[18px] overflow-hidden border border-[#E2E8F0] my-4 sm:my-8 shadow-[0_20px_50px_rgba(30,42,68,0.06)]">
          <div className="relative min-h-[280px] sm:min-h-[360px] lg:min-h-full v2-grain">
            <ImageMeta
              src={HERO.homeOfficePlants.src}
              alt={HERO.homeOfficePlants.alt}
              title={HERO.homeOfficePlants.title}
              width={HERO.homeOfficePlants.width}
              height={HERO.homeOfficePlants.height}
              credit={HERO.homeOfficePlants.credit}
              creator={HERO.homeOfficePlants.creator}
              schema
              className="absolute inset-0 w-full h-full object-cover v2-image-grade"
              testId="home-time-back-img"
            />
          </div>
          <div className="bg-[#F6F8FB] p-8 sm:p-12 flex flex-col justify-center">
            <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">The point of it all</p>
            <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-2 leading-[1.05]">
              Long lunches. Real conversations. Back on the calendar.
            </h2>
            <p className="text-[#475569] mt-4 leading-relaxed">
              The Pro Plan wasn&rsquo;t designed to fill your calendar with more content admin ,
              it was designed to <em className="text-[#1E2A44] not-italic font-semibold">empty it</em>.
              45 minutes of recording. A short weekly check-in. That&rsquo;s your entire content week.
            </p>
            <p className="text-[#475569] mt-4 leading-relaxed">
              What you do with the reclaimed hours is up to you. Coffee with a friend. Client work. Family. A walk.
              All of the above.
            </p>
          </div>
        </Reveal>
      </Section>

      {/* HOW IT WORKS */}
      <Section bg="white" testId="home-how">
        <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">How it works</p>
        <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-2">Three steps. Weekly, forever.</h2>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {STEPS.map(s => (
            <div key={s.n} className="bg-[#F6F8FB] rounded-[14px] p-6 sm:p-7 border border-[#E2E8F0]">
              <div className="font-display text-5xl font-bold text-[#BC516F] leading-none">{s.n}.</div>
              <h3 className="font-display font-semibold text-[#1E2A44] text-xl mt-4">{s.title}</h3>
              <p className="text-[#475569] mt-2 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* PROOF */}
      <Section bg="bg" testId="home-proof">
        <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight max-w-3xl">Quietly building authority for clients like you.</h2>
        <div className="mt-10 grid lg:grid-cols-3 gap-5">
          {TESTIMONIALS.map((t, i) => (
            <figure key={i} className="bg-white border border-[#E2E8F0] rounded-[14px] p-6 sm:p-7 shadow-[0_10px_30px_rgba(30,42,68,0.04)] flex flex-col">
              <Quote className="w-6 h-6 text-[#BC516F] mb-3" />
              <blockquote className="text-[#1E2A44] leading-relaxed text-[16px] sm:text-[17px] flex-1">{t.quote}</blockquote>
              <figcaption className="mt-5 pt-4 border-t border-[#E2E8F0]">
                <div className="font-display font-semibold text-[#1E2A44]">{t.name}</div>
                <div className="text-sm text-[#64748B]">{t.role}</div>
              </figcaption>
            </figure>
          ))}
        </div>

        {/* Authority Hub client tile */}
        <div className="mt-8 bg-white border border-[#E2E8F0] rounded-[14px] overflow-hidden shadow-[0_10px_30px_rgba(30,42,68,0.04)]">
          <div className="grid sm:grid-cols-5">
            <div className="sm:col-span-3 p-6 sm:p-8">
              <p className="text-xs uppercase tracking-widest text-[#BC516F] font-semibold">Client example</p>
              <h3 className="font-display font-bold text-[#1E2A44] text-2xl mt-2">Treat Your Business, Business Website</h3>
              <p className="text-[#475569] mt-3 leading-relaxed">
                A dedicated content website built by Audio &amp; Co that organises every episode, blog and video
                into a searchable listen-watch-learn library. The kind of compounding asset social posts can’t become.
              </p>
              <a
                href="https://thrive-businesscoaching.com"
                target="_blank" rel="noreferrer"
                className="mt-5 inline-flex items-center gap-2 text-[#BC516F] hover:text-[#A7468A] font-semibold"
                data-testid="client-example-link"
              >
                View the Hub <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <div className="sm:col-span-2 bg-gradient-to-br from-[#1E2A44] to-[#A7468A] p-8 flex items-center justify-center">
              <div className="text-white font-display text-3xl font-bold opacity-90 text-center leading-tight">
                Listen.<br/>Watch.<br/>Learn.
              </div>
            </div>
          </div>
        </div>
      </Section>

      <CompoundSection />
      <WhoWeWorkWith />
      <FounderStrip />

      {/* PRICING */}
      <Section bg="white" testId="home-pricing">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">Weekly Podcast Production (Pro Plan)</p>
          <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-2">One package. Everything included. 4-week minimum.</h2>
        </div>
        <div className="mt-10 max-w-2xl mx-auto bg-[#F6F8FB] border border-[#E2E8F0] rounded-[14px] p-8 sm:p-10 text-center shadow-[0_10px_30px_rgba(30,42,68,0.06)]">
          <div className="font-display text-5xl sm:text-6xl font-bold text-[#1E2A44]">
            £280<span className="text-2xl text-[#64748B] font-medium">/week</span>
          </div>
          <p className="text-[#64748B] mt-2">4-week minimum, cancel any time &nbsp;•&nbsp; billed monthly</p>
          <p className="text-sm text-[#64748B] mt-1">Approx. £1,213/month &nbsp;•&nbsp; 6 months recommended for compounding momentum</p>
          <div className="mt-8 flex justify-center">
            <CTABlock size="lg" align="center" testIdPrefix="pricing-cta" />
          </div>
        </div>
      </Section>

      {/* FAQ */}
      <Section bg="bg" testId="home-faq">
        <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight">Frequently asked questions</h2>
        <div className="mt-8 max-w-3xl space-y-3">
          {FAQS.map((f, i) => <FAQItem key={i} q={f.q} a={f.a} idx={i} />)}
        </div>
      </Section>

      {/* LATEST FROM THE BLOG */}
      <LatestFromBlog
        eyebrow="Read next"
        heading="From the blog"
        subheading="Straight-talking guides on SEO, websites, podcasts and content that actually gets found."
        bg="bg"
        testId="home-latest-blog"
      />

      {/* FINAL CTA */}
      <Section bg="navy" testId="home-final-cta" innerClassName="!py-16 sm:!py-20">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-display font-bold text-white text-3xl sm:text-4xl tracking-tight">Ready to be seen and heard, every week?</h2>
          <p className="text-white/75 mt-4 text-lg">30-minute call. We&rsquo;ll walk you through what the Pro Plan looks like for your business.</p>
          <div className="mt-7 flex justify-center">
            <CTABlock size="lg" align="center" invert testIdPrefix="final-cta" />
          </div>
        </div>
      </Section>

      {/* Exit-intent modal on Home, 30s delay per user's spec */}
      <NewsletterModal
        delayMs={30000}
        source="modal_home"
        heading="Before you go ,"
        subtext="Get one honest email a week from Jo. Small business visibility, straight talk."
        storageKey="nl_modal_home_dismissed_v1"
        testId="home-newsletter-modal"
      />
    </main>
  );
}

function FAQItem({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-[14px] overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 hover:bg-[#F6F8FB] transition"
        data-testid={`faq-toggle-${idx}`}
      >
        <span className="font-display font-semibold text-[#1E2A44] text-[17px]">{q}</span>
        <ChevronDown className={`w-5 h-5 text-[#64748B] shrink-0 transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 text-[#475569] leading-relaxed" data-testid={`faq-answer-${idx}`}>
          {a}
        </div>
      )}
    </div>
  );
}
